// ============================================================================
// ui/preview.js — the stage region: flat SVG fallback + persistent 3D host
// ============================================================================
// The stage is a LIVE 3D plate (mounted by the orchestrator into `el`), so this
// module's flat SVG has two jobs: (1) the INSTANT first frame painted before the
// Three.js chunk loads, and (2) the permanent WebGL-unsupported FALLBACK. It
// re-paints on edits (cheap) so the fallback stays current even while the 3D
// plate is the visible surface.
//
// The stage root doubles as the shell's `.ds-canvas` container (see shell.css):
// it is the framed workspace that hosts all the absolute chrome — the left
// slide-out, the top-right tool cluster, the zoom popover, the powered-by badge,
// and the status pill — which the orchestrator appends onto `el`.
//
// Per V2 §4: glass NEVER goes over this region — it's the single high-contrast
// area where the customer's artwork lives. Fit-to-view (zoom popover) is now the
// single "recenter" affordance — the old bottom-right reset pill was consolidated
// into it so the corner is free for the powered-by badge (#4/#6).
// ============================================================================

import { h } from './dom.js';
import { renderDesignToSVGString } from '../render-engine.js';
import { modelFor, productSupportsBodyColor } from '../products.js';
import { DEFAULT_BODY_COLOR, displayWeightText } from '../design-profile.js';

/** Debounce window for re-render on rapid edits (color drag, typing). */
const RENDER_DEBOUNCE_MS = 60;

/**
 * @param {object} ctx - orchestrator context ({ state, announce, status }).
 */
export function createPreview(ctx) {
  const svgHost = h('div', { class: 'wd-canvas-stage', 'data-wd-canvas-stage': '', role: 'img' });

  // The stage root IS the shell's framed canvas container (.ds-canvas): 100%×70vh
  // with the GD dark border. `.wd-stage-region` adds the WD centering + glow.
  const root = h('div', { class: 'wd-stage-region ds-canvas' }, svgHost);

  let debounceTimer = null;

  /** Compose the SVG for the current state and inject it (idempotent). */
  function paint() {
    const { state } = ctx;
    // Deterministic uid keyed to the design so re-injected markup is stable and
    // ids don't leak/accumulate across renders.
    const svg = renderDesignToSVGString(state.designProfile, {
      weight: state.ui.weight,
      size: 520,
      uid: 'wd-preview',
      // Place the elements on THIS product's zone. The chrome behind them is still
      // the bumper's rings/hub for every product — pre-existing (the dumbbell has
      // always drawn it too) and only visible in the flat frame before the 3D
      // mounts, or in the no-WebGL fallback. Worth fixing; not this change.
      product: modelFor(state.ui.product),
      // The dye ONLY on a product that can wear it — the same gate the 3D texture, the layout
      // thumbnails, the card preview and the mobile pager all apply. Missing here, this was the
      // one surface that resolved the contextual-contrast view against the STORED dye on every
      // product, so the no-WebGL fallback and the pre-mount first frame peeled a yellow plate's
      // rings onto a black dumbbell (Ryan 2026-08-01).
      bodyColorOverride: productSupportsBodyColor(state.ui.product) ? undefined : DEFAULT_BODY_COLOR,
    });
    svgHost.innerHTML = svg;
    // The weight is stored in POUNDS and only LABELLED in the display unit — see design-profile's
    // "UNIT IS A LABEL, NOT A WEIGHT". Reading `ui.weight` raw here would announce "45 kg".
    svgHost.setAttribute('aria-label', `${state.ui.productKind || 'Custom plate'} preview — ${displayWeightText(state.ui.weight, state.ui.unit)} ${state.ui.unit}`);
  }

  /** Debounced re-render — call after any design mutation. */
  function requestRender() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(paint, RENDER_DEBOUNCE_MS);
  }

  return { el: root, svgHost, paint, requestRender };
}
